import { DropdownSelectItemType, PresetRange } from '@dixa/lib-shared-habla';
import {
  SearchFilterField,
  SearchFilters,
  SearchFilterValueDatePresetKey,
} from 'lib-search-types';
import { filterFieldCatalogue } from 'lib-search-ui';
import { ConversationStatus } from 'lib-shared-types-api';
import moment from 'moment-timezone';

import { conversationStatusAttributes } from 'utils/conversation';

/** The list of filter fields available as quick filters. */
export const quickFilterFields: ReadonlySet<string> = new Set([
  'initial_direction',
  'status',
  'agent_id',
  'initial_channel',
  'requested_by',
  'queue_id',
  'tags',
]);

/** A map of static filter fields and items available to select in the dropdowns. */
export const filterItems: Record<
  'initial_direction' | 'status',
  DropdownSelectItemType[]
> = {
  initial_direction: [
    { id: 'inbound', name: 'Inbound' },
    { id: 'outbound', name: 'Outbound' },
    { id: 'internal', name: 'Internal' },
  ],
  status: (
    Object.keys(conversationStatusAttributes) as readonly ConversationStatus[]
  )
    .filter((status) => status !== 'awaitingpending')
    .map((status) => ({
      id: status,
      name: conversationStatusAttributes[status].label,
    })),
};

/** The filter fields supported for filtering on the search page, in the exact order that they
 * should be displayed on the search page. */
export const filterFields = [
  'tags',
  'queue_id',
  'agent_id',
  'requested_by',
  'status',
  'initial_channel',
  'initial_direction',
  'duration',
  'note_message',
  'note_author_id',
  'message_author_id',
  'numeric_id',
  ...(Object.entries(filterFieldCatalogue.conversations)
    .filter(([, { type }]) => type === 'date')
    .map(([field]) => field) as SearchFilterField<'conversations'>[]),
] satisfies SearchFilterField<'conversations'>[];

/** A moment object for today's date, starting at 12 AM */
const todayStart = moment().startOf('day');
/** A moment object for today's date, ending at 23:59 PM */
const todayEnd = moment().endOf('day');

/** A list of date range presets used for quick date selections. */
export const dateRangePresets: PresetRange[] = [
  {
    key: 'today',
    label: 'Today',
    startDate: todayStart,
    endDate: todayEnd,
  },
  {
    key: 'yesterday',
    label: 'Yesterday',
    startDate: todayStart.clone().subtract(1, 'day'),
    endDate: todayEnd.clone().subtract(1, 'day'),
  },
  {
    key: 'last_7_days',
    label: 'Last 7 days',
    startDate: todayStart.clone().subtract(7, 'day'),
    endDate: todayEnd,
  },
  {
    key: 'last_4_weeks',
    label: 'Last 4 weeks',
    startDate: todayStart.clone().subtract(4, 'week'),
    endDate: todayEnd,
  },
  {
    key: 'last_3_months',
    label: 'Last 3 months',
    startDate: todayStart.clone().subtract(3, 'month'),
    endDate: todayEnd,
  },
] satisfies Array<PresetRange & { key: SearchFilterValueDatePresetKey }>;

/** The initial search filters configuration state. */
export const filtersInitialState: SearchFilters<'conversations'> = {
  strategy: 'all',
  filters: [],
};

/** Error messages displayed when validating filter conditions. */
export const filterValidationErrors = {
  noValue: 'Please select at least one value or remove the filter',
  noDate: 'Please select a date or remove the filter',
  invalidRange: 'Please select both dates or use a different operator',
};
